/**
 * Turns a shift on a date into a span on the absolute-minute line, so two
 * shifts on different dates can be compared, and measures the signed gap
 * between two such spans (PLAN.md §3.3).
 *
 * The invariant that must not break: **numbers only, no verdicts.** This
 * module answers "how many minutes", never "is that allowed" — no warn, no
 * block, no "clean fit", no threshold. Those questions belong to
 * `src/rules/`, which stays empty until Phase 6, precisely so that "how many
 * minutes" and "is that allowed" stay separable and independently testable.
 *
 * This file imports nothing from `src/data/`, the same rule `roster.ts`
 * follows: a `RosterShift` arrives as an argument, so the roster's shift
 * times remain configuration rather than a constant baked into the engine.
 */

import { absoluteMinutes } from './civil'
import type { IsoDate, RosterShift } from './types'
import { MINUTES_PER_DAY } from './types'

/** A shift placed on the absolute-minute line, so two shifts on different dates compare. */
export interface ShiftInterval {
  startAbs: number
  endAbs: number
}

/**
 * Places a shift worked on `date` onto the absolute-minute line.
 *
 * `startAbs` is `date` at `shift.startMin`. `endAbs` is `date` at
 * `shift.endMin` — plus a whole day whenever `shift.endMin <= shift.startMin`,
 * which is how the night shift's midnight crossing is derived rather than
 * stored: N is 2100–0700, and its `endMin` of 420 is *less* than its 1260
 * `startMin`. A shift always belongs to its start date (`RosterShift` in
 * `./types`); this is the one place that fact turns into arithmetic.
 */
export function shiftInterval(date: IsoDate, shift: RosterShift): ShiftInterval {
  const crossesMidnight = shift.endMin <= shift.startMin
  return {
    startAbs: absoluteMinutes(date, shift.startMin),
    endAbs: absoluteMinutes(date, shift.endMin) + (crossesMidnight ? MINUTES_PER_DAY : 0),
  }
}

/**
 * The signed gap, in minutes, from the end of `earlier` to the start of
 * `later`.
 *
 * Measures **wall-clock** time, not elapsed time (PLAN.md Q2): nothing here
 * ever converts to an instant, so a break spanning the spring-forward night
 * is nine real hours but still measures as ten — which is how the roster and
 * everyone working it talks about a break, and is intended rather than an
 * oversight.
 *
 * The sign is the whole point and is never clamped away: a **negative**
 * result means the two shifts overlap — `later` starts before `earlier` has
 * finished, which is physically impossible to work — while a small positive
 * result is merely a short break. The caller (Phase 6) is the one that tells
 * those apart; this function only reports which it is.
 */
export function gapMinutes(earlier: ShiftInterval, later: ShiftInterval): number {
  return later.startAbs - earlier.endAbs
}
